import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { Ban, CircleCheck, CircleHelp, CircleX, GitCompare, Loader, Sparkles, Trash2, Undo2 } from 'lucide-react'
import { useMemo, type ReactNode } from 'react'

import { Button } from '@krp/ui-kit'

import type { LineField, MatchStatus } from '../model/types'
import type { LineCellVm, LineRowVm, LinesActions } from '../model/viewModels'

/**
 * Таблица строк поставки. Ширина колонок меняется обработчиком самой таблицы
 * (`header.getResizeHandler()`) — исключение CLAUDE.md §7: обработчик ведёт
 * собственное состояние ресайза таблицы, подменять его своим жестом незачем.
 * Ячейки — поля ввода: набранное уходит в черновик, сохраняет debounce-listener.
 */
const MATCH_ICONS: Record<MatchStatus, ReactNode> = {
  Matched: <CircleCheck size={14} aria-hidden="true" />,
  Ambiguous: <CircleHelp size={14} aria-hidden="true" />,
  New: <Sparkles size={14} aria-hidden="true" />,
  NotMatched: <CircleX size={14} aria-hidden="true" />,
}

const DRAFT_TEXT = {
  pending: 'Изменено, сохранится через полсекунды',
  saving: 'Сохраняем…',
  failed: 'Не сохранено',
  unknown: 'Исход сохранения неизвестен',
  conflict: 'Строку изменили на сервере',
} as const

function NumberCell({
  cell,
  label,
  disabled,
  onChange,
}: {
  cell: LineCellVm
  label: string
  disabled: boolean
  onChange: (value: string) => void
}) {
  return (
    <>
      <input
        className="onec-cell-input"
        aria-label={label}
        inputMode="decimal"
        value={cell.value}
        disabled={disabled}
        data-changed={cell.changed}
        onChange={(event) => onChange(event.target.value)}
      />
      {cell.changed && <span className="onec-muted">на сервере {cell.server === '' ? '—' : cell.server}</span>}
    </>
  )
}

/**
 * Конфликт и отказ строки с кнопками решения. Показывается НАД таблицей: на
 * телефоне последняя колонка за краем экрана, и решение там не найти.
 */
export function DraftNote({ row, actions }: { row: LineRowVm; actions: LinesActions }) {
  const draft = row.draft
  if (draft === null) return null
  const exceeds = draft.code === 'allocation-exceeds-quantity'
  return (
    <div className="onec-draft" data-state={draft.state} role={draft.state === 'pending' || draft.state === 'saving' ? 'status' : 'alert'}>
      {draft.state === 'saving' ? <Loader size={14} aria-hidden="true" /> : draft.state === 'conflict' ? <GitCompare size={14} aria-hidden="true" /> : null}
      <span>{DRAFT_TEXT[draft.state]}</span>
      {draft.message !== null && <span>{draft.message}</span>}
      {(draft.state === 'failed' || draft.state === 'unknown') && (
        <Button variant="secondary" onClick={() => actions.retry(row.id)}>
          {draft.state === 'unknown' ? 'Проверить исход' : 'Повторить'}
        </Button>
      )}
      {draft.state === 'conflict' && exceeds && (
        <>
          <Button variant="secondary" onClick={() => actions.keepMine(row.id, 'Clear')}>
            Сохранить и очистить распределение
          </Button>
          <Button variant="secondary" onClick={() => actions.keepMine(row.id, 'Proportional')}>
            Сохранить и уменьшить пропорционально
          </Button>
        </>
      )}
      {draft.state === 'conflict' && !exceeds && (
        <Button variant="secondary" onClick={() => actions.keepMine(row.id)}>
          Оставить моё
        </Button>
      )}
      {(draft.state === 'conflict' || draft.state === 'failed' || draft.state === 'unknown') && (
        <Button variant="ghost" onClick={() => actions.takeServer(row.id)}>
          Взять с сервера
        </Button>
      )}
    </div>
  )
}

const helper = createColumnHelper<LineRowVm>()

export function SupplyLinesTable({
  rows,
  editable,
  actions,
}: {
  rows: readonly LineRowVm[]
  editable: boolean
  actions: LinesActions
}) {
  const columns = useMemo(() => {
    const edit = (row: LineRowVm, field: LineField) => (value: string) => actions.edit(row.id, field, value)
    return [
      helper.accessor('lineNumber', { header: '№', size: 48 }),
      helper.display({
        id: 'name',
        header: 'Наименование',
        size: 240,
        cell: ({ row: { original: row } }) => (
          <div>
            <div>{row.name}</div>
            <div className="onec-muted">
              {[row.article !== null ? `арт. ${row.article}` : null, row.barcode, row.unitName].filter((part) => part !== null).join(' · ')}
            </div>
            {row.oneCName !== null && row.oneCName !== row.name && <div className="onec-muted">в 1С: {row.oneCName}</div>}
          </div>
        ),
      }),
      helper.display({
        id: 'match',
        header: 'Сопоставление',
        size: 200,
        cell: ({ row: { original: row } }) => (
          <div className="onec-screen">
            <span className="onec-badge" data-tone={row.match.status === 'Matched' ? 'done' : row.match.status === 'New' ? 'ready' : 'failed'}>
              {MATCH_ICONS[row.match.status]}
              {row.match.label}
              {row.match.detail !== null ? ` · ${row.match.detail}` : ''}
            </span>
            {editable && row.match.status !== 'Matched' && row.candidates.length > 0 && (
              <ul className="onec-steps" aria-label="Кандидаты">
                {row.candidates.map((candidate) => (
                  <li key={candidate.productId}>
                    <Button variant="ghost" onClick={() => actions.override(row.id, candidate.productId)}>
                      {candidate.name} · {candidate.score}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            {editable && (
              <div className="onec-draft">
                <input
                  className="onec-cell-input"
                  aria-label={`Ключ товара 1С для строки ${row.lineNumber}`}
                  placeholder="ключ товара 1С"
                  value={row.override}
                  onChange={(event) => actions.overrideTyped(row.id, event.target.value)}
                />
                <Button variant="ghost" onClick={() => actions.override(row.id)} disabled={row.override.trim() === ''}>
                  Привязать
                </Button>
                {row.match.status !== 'New' && (
                  <Button variant="ghost" onClick={() => actions.markNew(row.id)}>
                    Новый товар
                  </Button>
                )}
                {row.match.status !== 'NotMatched' && (
                  <Button variant="ghost" icon={<Undo2 size={14} aria-hidden="true" />} onClick={() => actions.resetMatch(row.id)}>
                    Сбросить
                  </Button>
                )}
              </div>
            )}
          </div>
        ),
      }),
      helper.display({
        id: 'quantity',
        header: 'Количество',
        size: 110,
        cell: ({ row: { original: row } }) => (
          <NumberCell cell={row.quantity} label={`Количество, строка ${row.lineNumber}`} disabled={!editable || row.ignored} onChange={edit(row, 'quantity')} />
        ),
      }),
      helper.display({
        id: 'price',
        header: 'Цена',
        size: 110,
        cell: ({ row: { original: row } }) => (
          <NumberCell cell={row.purchasePrice} label={`Цена, строка ${row.lineNumber}`} disabled={!editable || row.ignored} onChange={edit(row, 'purchasePrice')} />
        ),
      }),
      helper.display({
        id: 'amount',
        header: 'Сумма',
        size: 120,
        cell: ({ row: { original: row } }) => (
          <NumberCell cell={row.amount} label={`Сумма, строка ${row.lineNumber}`} disabled={!editable || row.ignored} onChange={edit(row, 'amount')} />
        ),
      }),
      helper.display({
        id: 'allocated',
        header: 'Распределено',
        size: 110,
        cell: ({ row: { original: row } }) => (
          <span>
            {row.allocated} {row.fullyAllocated ? '· полностью' : '· не всё'}
          </span>
        ),
      }),
      helper.display({
        id: 'notes',
        header: 'Примечание',
        size: 160,
        cell: ({ row: { original: row } }) => (
          <input
            className="onec-cell-input"
            aria-label={`Примечание, строка ${row.lineNumber}`}
            value={row.notes.value}
            disabled={!editable}
            data-changed={row.notes.changed}
            onChange={(event) => actions.edit(row.id, 'notes', event.target.value)}
          />
        ),
      }),
      helper.display({
        id: 'actions',
        header: 'Состояние',
        size: 220,
        cell: ({ row: { original: row } }) => (
          <div className="onec-screen">
            {row.draft !== null && (
              <span className="onec-draft" data-state={row.draft.state}>
                {row.draft.state === 'saving' && <Loader size={14} aria-hidden="true" />}
                {DRAFT_TEXT[row.draft.state]}
              </span>
            )}
            {row.ignored && <span className="onec-badge" data-tone="partial">Исключена из проведения</span>}
            {editable && (
              <div className="onec-draft">
                <Button variant="ghost" icon={<Ban size={14} aria-hidden="true" />} onClick={() => actions.toggleIgnored(row.id)}>
                  {row.ignored ? 'Вернуть' : 'Исключить'}
                </Button>
                <Button variant="ghost" icon={<Trash2 size={14} aria-hidden="true" />} onClick={() => actions.remove(row.id)}>
                  Удалить
                </Button>
              </div>
            )}
          </div>
        ),
      }),
    ]
  }, [actions, editable])

  const data = useMemo(() => [...rows], [rows])
  // Таблица сама ведёт состояние ресайза. `useReactTable` несовместим с React Compiler (правило `incompatible-library`):
  // после его подключения компонент будет пропущен, поэтому `useMemo` для `data` и `columns` здесь остаётся обязательным.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    columnResizeMode: 'onChange',
  })

  return (
    <div className="onec-scroll">
      <table className="onec-table onec-lines" style={{ width: table.getTotalSize() }}>
        <thead>
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id}>
              {group.headers.map((header) => (
                <th key={header.id} style={{ width: header.getSize() }}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  <span
                    className="onec-resizer"
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={`Ширина колонки «${String(header.column.columnDef.header)}»`}
                    data-resizing={header.column.getIsResizing()}
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                  />
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className={row.original.ignored ? 'onec-row-ignored' : undefined}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} style={{ width: cell.column.getSize() }} data-label={String(cell.column.columnDef.header)}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
